import { useContext, useState } from "react";
import {
    Input,
    Button,
    Card,
    Typography,
    Modal,
    Spin,
    message,
} from "antd";
import {
    CalendarOutlined,
    FileTextOutlined,
    ClockCircleOutlined,
    SearchOutlined,
    UserOutlined,
    IdcardOutlined,
    FieldTimeOutlined,
} from "@ant-design/icons";
import { AuthContext } from "../../context/AuthContext";
import axios from "axios";
import LeaveCalendar from "../../components/employee/LeaveCalendar";
import ViewOneEmployeeRecord from "../../components/hr/ViewOneEmployeeRecord";
import ViewOneEmpPerm from "../../components/ViewOneEmpPerm";
import ViewEmployeeAttendance from "../hr/ViewEmployeeAttendance";
import ViewEmpAttendance from "../../components/attendance/viewEmpAttendance";

const { Title, Text } = Typography;

export default function QuickView() {
    const [empCode, setEmpCode] = useState("");
    const { authData, backUrl } = useContext(AuthContext);
    const [employee, setEmployee] = useState(null);
    const [loading, setLoading] = useState(false);
    const [activeModal, setActiveModal] = useState(null); // 'calendar' | 'leaves' | 'permissions' | 'attendance'

    const fetchEmployees = async () => {
        if (!empCode.trim()) {
            message.warning("Please enter an employee code.");
            return;
        }
        setLoading(true);
        try {
            const res = await axios.get(
                `${backUrl}/api/emp/employees?emp_code=${empCode}`,
                {
                    headers: { Authorization: `Bearer ${authData?.access}` },
                }
            );
            const result = res.data.results[0];
            if (!result) {
                message.error("No employee found with that code.");
                setEmployee(null);
            } else {
                setEmployee(result);
                message.success("Employee found!");
            }
        } catch (err) {
            message.error("Failed to fetch employee. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const emp = employee?.employee ?? employee;

    const actionButtons = [
        {
            key: "calendar",
            label: "Calendar",
            icon: <CalendarOutlined />,
            color: "#0e497b",
            bg: "#ffffff",
        },
        {
            key: "leaves",
            label: "Leaves",
            icon: <FileTextOutlined />,
            color: "#1a7a4a",
            bg: "#ffffff",
        },
        {
            key: "permissions",
            label: "Permissions",
            icon: <ClockCircleOutlined />,
            color: "#8b4a00",
            bg: "#ffffff",
        },
        {
            key: "attendance",
            label: "Attendance",
            icon: <FieldTimeOutlined />,
            color: "#6b21a8",
            bg: "#ffffff",
        },
    ];

    return (
        <>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600&family=Syne:wght@600;700&display=swap');

                .qv-wrapper {
                    min-height: 100vh;
                    background: #ffffff;
                    display: flex;
                    align-items: flex-start;
                    justify-content: center;
                    padding: 48px 16px;
                    font-family: 'DM Sans', sans-serif;
                }

                .qv-card {
                    width: 100%;
                    background: #ffffff;
                    border-radius: 20px;
                    box-shadow: 0 4px 24px rgba(14, 73, 123, 0.10);
                    padding: 36px 32px 32px;
                    border: 1px solid #ffffff;
                }

                .qv-header {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                    margin-bottom: 28px;
                }

                .qv-header-icon {
                    width: 44px;
                    height: 44px;
                    background: #0e497b;
                    border-radius: 12px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    color: white;
                    font-size: 20px;
                    flex-shrink: 0;
                }

                .qv-title {
                    font-size: 20px !important;
                }

                .qv-subtitle {
                    font-size: 12px;
                    color: #7a95ab;
                    margin-top: 2px;
                    display: block;
                }

                .qv-divider {
                    height: 1px;
                    background: #ffffff;
                    margin-bottom: 24px;
                }

                .qv-input-wrap {
                    position: relative;
                    margin-bottom: 14px;
                }

                .qv-input {
                    border-radius: 10px !important;
                    height: 46px !important;
                    font-size: 14px !important;
                    border-color: #797979 !important;
                    font-family: 'DM Sans', sans-serif !important;
                    padding-left: 14px !important;
                }

                .qv-input:focus {
                    border-color: #0e497b !important;
                    box-shadow: 0 0 0 2px rgba(14, 73, 123, 0.10) !important;
                }

                .qv-btn-submit {
                    width: 100%;
                    height: 36px !important;
                    border-radius: 10px !important;
                    background: #0e497b !important;
                    border-color: #0e497b !important;
                    font-family: 'Syne', sans-serif !important;
                    font-size: 14px !important;
                    font-weight: 600 !important;
                    letter-spacing: 0.3px;
                    transition: background 0.2s, transform 0.1s !important;
                }

                .qv-btn-submit:hover {
                    background: #0a3560 !important;
                    transform: translateY(-1px);
                }

                .qv-btn-submit:active {
                    transform: translateY(0);
                }

                /* Employee Result Card */
                .qv-emp-card {
                    margin-top: 24px;
                    background: linear-gradient(135deg, #0e497b 0%, #1a6baa 100%);
                    border-radius: 14px;
                    padding: 18px 20px;
                    color: white;
                    display: flex;
                    align-items: center;
                    gap: 14px;
                    animation: slideIn 0.3s ease;
                }

                @keyframes slideIn {
                    from { opacity: 0; transform: translateY(10px); }
                    to { opacity: 1; transform: translateY(0); }
                }

                .qv-emp-avatar {
                    width: 48px;
                    height: 48px;
                    background: rgba(255, 255, 255, 0.2);
                    border-radius: 12px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 22px;
                    flex-shrink: 0;
                    backdrop-filter: blur(4px);
                }

                .qv-emp-name {
                    font-family: 'Syne', sans-serif;
                    font-size: 16px;
                    font-weight: 700;
                    line-height: 1.2;
                    margin: 0;
                }

                .qv-emp-meta {
                    font-size: 12px;
                    opacity: 0.75;
                    margin-top: 3px;
                }

                /* Action Buttons — 4 columns when employee is loaded */
                .qv-actions {
                    margin-top: 18px;
                    display: grid;
                    grid-template-columns: repeat(4, 1fr);
                    gap: 10px;
                    animation: slideIn 0.4s ease 0.1s both;
                }

                .qv-action-btn {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    gap: 7px;
                    padding: 16px 8px;
                    border-radius: 12px;
                    cursor: pointer;
                    border: 1.5px solid transparent;
                    transition: all 0.2s ease;
                    font-family: 'DM Sans', sans-serif;
                }

                .qv-action-btn:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 6px 16px rgba(0,0,0,0.10);
                }

                .qv-action-btn:active {
                    transform: translateY(0);
                }

                .qv-action-icon {
                    font-size: 22px;
                    line-height: 1;
                }

                .qv-action-label {
                    font-size: 11px;
                    font-weight: 600;
                    letter-spacing: 0.2px;
                    text-align: center;
                }

                /* Modal */
                .qv-modal .ant-modal-content {
                    border-radius: 16px !important;
                    overflow: hidden;
                }

                .qv-modal .ant-modal-header {
                    background: #0e497b !important;
                    padding: 16px 24px !important;
                    border-bottom: none !important;
                }

                .qv-modal .ant-modal-title {
                    color: white !important;
                    font-family: 'Syne', sans-serif !important;
                    font-size: 16px !important;
                    font-weight: 700 !important;
                }

                .qv-modal .ant-modal-close {
                    color: white !important;
                    top: 14px !important;
                }

                .qv-modal .ant-modal-body {
                    padding: 0 !important;
                    max-height: 75vh;
                    overflow-y: auto;
                }
            `}</style>

            <center>
                <div style={{ width: 480 }} className="qv-wrapper">
                    <div>
                        {/* Header */}
                        <div className="qv-header">
                            <div className="qv-header-icon">
                                <IdcardOutlined />
                            </div>
                            <div>
                                <Title className="qv-title">Quick View</Title>
                            </div>
                        </div>

                        <div className="qv-divider" />

                        {/* Input */}
                        <div className="qv-input-wrap">
                            <Input
                                className="qv-input"
                                placeholder="Enter Employee Code"
                                value={empCode}
                                onChange={(e) => setEmpCode(e.target.value)}
                                onPressEnter={fetchEmployees}
                                prefix={<UserOutlined style={{ color: "#aac4d8" }} />}
                            />
                        </div>

                        <Button
                            type="primary"
                            className="qv-btn-submit"
                            icon={<SearchOutlined />}
                            onClick={fetchEmployees}
                            loading={loading}
                        >
                            Search Employee
                        </Button>

                        {/* Employee Card */}
                        {employee && (
                            <>
                                <div className="qv-emp-card">
                                    <div className="qv-emp-avatar">
                                        <UserOutlined />
                                    </div>
                                    <div>
                                        <div className="qv-emp-name">
                                            {emp?.first_name || emp?.name || "Employee"}
                                            {emp?.last_name ? ` ${emp.last_name}` : ""}
                                        </div>
                                        <div className="qv-emp-meta">
                                            <div>{emp?.emp_code || empCode}</div>
                                            <div>{emp?.designation_name} - {emp?.department_name}</div>
                                            <div>{emp?.organisation_name}</div>
                                        </div>
                                    </div>
                                </div>

                                {/* Action Buttons */}
                                <div className="qv-actions">
                                    {actionButtons.map((btn) => (
                                        <div
                                            key={btn.key}
                                            className="qv-action-btn"
                                            style={{
                                                background: btn.bg,
                                                borderColor: btn.bg,
                                                color: btn.color,
                                            }}
                                            onClick={() => setActiveModal(btn.key)}
                                        >
                                            <span
                                                className="qv-action-icon"
                                                style={{ color: btn.color }}
                                            >
                                                {btn.icon}
                                            </span>
                                            <span
                                                className="qv-action-label"
                                                style={{ color: btn.color }}
                                            >
                                                {btn.label}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </>
                        )}
                    </div>
                </div>

                {/* Calendar Modal */}
                <Modal
                    className="qv-modal"
                    title={<><CalendarOutlined style={{ marginRight: 8 }} />Calendar</>}
                    open={activeModal === "calendar"}
                    onCancel={() => setActiveModal(null)}
                    footer={null}
                    width={1300}
                    destroyOnClose
                >
                    <LeaveCalendar employee={employee} />
                </Modal>

                {/* Leaves Modal */}
                <Modal
                    className="qv-modal"
                    title={<><FileTextOutlined style={{ marginRight: 8 }} />Leave Records</>}
                    open={activeModal === "leaves"}
                    onCancel={() => setActiveModal(null)}
                    footer={null}
                    width={1300}
                    destroyOnClose
                >
                    {employee && (
                        <ViewOneEmployeeRecord employee={employee} isProfile={false} />
                    )}
                </Modal>

                {/* Permissions Modal */}
                <Modal
                    className="qv-modal"
                    title={<><ClockCircleOutlined style={{ marginRight: 8 }} />Permissions</>}
                    open={activeModal === "permissions"}
                    onCancel={() => setActiveModal(null)}
                    footer={null}
                    width={1300}
                    destroyOnClose
                >
                    {employee && (
                        <ViewOneEmpPerm employee={employee} isProfile={false} />
                    )}
                </Modal>

                {/* Attendance Modal */}
                <Modal
                    className="qv-modal"
                    title={<><FieldTimeOutlined style={{ marginRight: 8 }} />View Attendance</>}
                    open={activeModal === "attendance"}
                    onCancel={() => setActiveModal(null)}
                    footer={null}
                    width={1300}
                    destroyOnClose
                >
                    {employee && (
                        <ViewEmpAttendance employee={employee} isProfile={false} />
                    )}
                </Modal>
            </center>

        </>
    );
}